import '@react-pdf-viewer/core/lib/styles/index.css';

import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Backdrop,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import { ProgressBar, RenderPageProps, Viewer } from '@react-pdf-viewer/core';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';

import { ApiRoutes } from '../../../../config/ApiRoutes';
import {
  AllowedImageFileTypes,
  AllowedPdfFileTypes,
  Config,
} from '../../../../config/Constants';
import NavRoutes from '../../../../config/routes/NavRoutes';
import { useCustomStore } from '../../../../hooks';
import { SnackBarConfig } from '../../../../utils/SnackBarConfig';
import useStyles from '../documentsStyle';
import uploadIcon from './../../../../assets/images/upload.svg';

const TableBodyContent: React.FC = () => {
  const classes = useStyles();
  const [url, setUrl] = useState('');
  const [fileId, setFileId] = useState('');
  const [fileType, setFileType] = useState('');
  const [previewFileName, setFileName] = useState('');
  const [openPreview, setOpenPreview] = useState(false);
  const { authStore, documentsStore } = useCustomStore();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const handleDownload = (id: string) => {
    const tokens = authStore.tokens;
    const url = `${Config.API_URL}${
      ApiRoutes.Documents.downloadDocument.Endpoint
    }?id=${id}&accessToken=${tokens?.accessToken ?? ''}`;
    window.open(url);
  };

  const handleOpen = (id: string, fileName: string) => {
    const tokens = authStore.tokens;
    const url = `${Config.API_URL}${
      ApiRoutes.Documents.downloadDocument.Endpoint
    }?id=${id}&accessToken=${tokens?.accessToken ?? ''}`;
    setUrl(url);
    setFileId(id);
    setFileName(fileName);
    setFileType(fileName.split('.').pop()?.toLowerCase() || '');
    setOpenPreview(true);
  };

  const handleClose = () => {
    setOpenPreview(false);
  };
  const getDocumentList = useCallback(async () => {
    documentsStore.resetDocumentsList();
    const documentResp = await documentsStore.getDocuments({
      docType: documentsStore.ApiCallType.docType,
      id: documentsStore.ApiCallType.id,
    });
    if (documentResp.isErr()) {
      enqueueSnackbar(String(documentResp.error.message), SnackBarConfig('e'));
      documentsStore.setDocumentResponse([]);
    }
  }, [documentsStore, enqueueSnackbar]);
  const onDriverFilter = useCallback(
    (driver: { id: string; name: string }) => {
      documentsStore.setFilters({
        ...documentsStore.Filters,
        driver: driver,
        driverName: '',
        nextLink: '',
      });
      getDocumentList();
    },
    [documentsStore, getDocumentList],
  );

  const onUnitFilter = useCallback(
    (unit: { id: string; vim: string }) => {
      documentsStore.setFilters({
        ...documentsStore.Filters,
        nextLink: '',
        unit: unit,
        vinNumber: '',
      });
      getDocumentList();
    },
    [documentsStore, getDocumentList],
  );

  const onTagFilter = useCallback(
    (tag: string) => {
      if (documentsStore.Filters.tags.indexOf(tag.trim()) !== -1) {
        return;
      }
      documentsStore.setFilters({
        ...documentsStore.Filters,
        nextLink: '',
        tags: [...documentsStore.Filters.tags, tag.trim()],
      });
      getDocumentList();
    },
    [documentsStore, getDocumentList],
  );

  return (
    <>
      <TableBody>
        {documentsStore.DocumentsList ? (
          documentsStore.DocumentsList.length ? (
            documentsStore.DocumentsList.map((row, index) => (
              <TableRow key={index.toString()} className={classes.tableRow}>
                <TableCell>
                  <Tooltip title={row.fileName} placement="top">
                    <Typography
                      component={'a'}
                      onClick={() => handleOpen(row.id, row.fileName)}
                      target={'_blank'}
                      style={{
                        color: '#0061FF',
                        cursor: 'pointer',
                        fontFamily: 'FiraSans-SemiBold',
                        fontSize: '1rem !important',
                        textDecoration: 'underline',
                        width: '350px',
                        wordBreak: 'break-all',
                      }}>
                      {row.fileName
                        ? row.fileName.length > 30
                          ? row.fileName.substring(0, 30).concat('...')
                          : row.fileName
                        : ''}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  {row.tags &&
                  typeof row.tags === 'string' &&
                  row.tags.trim() !== ''
                    ? row.tags.split(',').map((tag: string, idx: number) => (
                        <Tooltip
                          key={idx}
                          title={'Filter for this tag'}
                          placement="top">
                          <Chip
                            key={idx}
                            label={tag.trim()}
                            size="small"
                            style={{
                              backgroundColor: '#E8E8E8',
                              color: '#979598 !important',
                              fontWeight: 600,
                              margin: '2px',
                            }}
                            onClick={() => onTagFilter(tag)}
                          />
                        </Tooltip>
                      ))
                    : '-'}
                </TableCell>
                <TableCell>
                  {row.uploadedOn
                    ? moment(row.uploadedOn).format('MM/DD/YYYY')
                    : '-'}
                </TableCell>
                {documentsStore.ApiCallType.docType !== 'driver' && (
                  <TableCell>
                    <span
                      style={{
                        alignItems: 'center',
                        display: 'flex',
                        gap: 4,
                        justifyContent: 'space-between',
                      }}>
                      <span>{row.driverName ? row.driverName : '-'}</span>
                      {row.driverName && (
                        <span
                          style={{
                            display: 'flex',
                            gap: 4,
                            justifyContent: 'flex-end',
                          }}>
                          {!documentsStore.Filters.driver && (
                            <Tooltip
                              title={'Filter for this Driver'}
                              placement="top">
                              <IconButton
                                size="small"
                                aria-label="Filter for this Driver"
                                onClick={() => {
                                  onDriverFilter({
                                    id: row.contactId,
                                    name: row.driverName ?? '',
                                  });
                                }}
                                disabled={documentsStore.Loading}>
                                <FilterListIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip
                            title={'View this Driver details'}
                            placement="top">
                            <IconButton
                              size="small"
                              aria-label="View this Driver details"
                              onClick={() => {
                                const isDriver =
                                  authStore.UserAccessType == 'driver';
                                const route = isDriver
                                  ? `/${NavRoutes.PRIVATE.LEFT_NAV.PROFILE}`
                                  : `/${NavRoutes.PRIVATE.LEFT_NAV.DRIVER}/${NavRoutes.PRIVATE.DRIVERS.PROFILE}/${row.contactId}`;
                                navigate(route);
                              }}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </span>
                      )}
                    </span>
                  </TableCell>
                )}
                {documentsStore.ApiCallType.docType !== 'unit' && (
                  <TableCell>
                    <span
                      style={{
                        alignItems: 'center',
                        display: 'flex',
                        gap: 4,
                        justifyContent: 'space-between',
                      }}>
                      <span>{row.unitVimNumber ? row.unitVimNumber : '-'}</span>
                      {row.unitVimNumber && (
                        <span
                          style={{
                            display: 'flex',
                            gap: 4,
                            justifyContent: 'flex-end',
                          }}>
                          {!documentsStore.Filters.unit && (
                            <Tooltip
                              title={'Filter for this Unit'}
                              placement="top">
                              <IconButton
                                size="small"
                                aria-label="Filter for this Unit"
                                onClick={() => {
                                  onUnitFilter({
                                    id: row.unitId,
                                    vim: row.unitVimNumber ?? '',
                                  });
                                }}
                                disabled={documentsStore.Loading}>
                                <FilterListIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip
                            title={'View this Unit details'}
                            placement="top">
                            <IconButton
                              size="small"
                              aria-label="View this Unit details"
                              onClick={() => {
                                const route = `/${NavRoutes.PRIVATE.LEFT_NAV.FLEET}/${NavRoutes.PRIVATE.FLEET.UPDATE}/${row.unitId}`;
                                navigate(route);
                              }}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </span>
                      )}
                    </span>
                  </TableCell>
                )}
                <TableCell>{row.fileSize ? row.fileSize : '-'}</TableCell>
                <TableCell align="center">
                  <Grid item style={{ position: 'relative' }}>
                    <Box
                      id={'upload'}
                      className={classes.uploadDoc}
                      onClick={() => handleDownload(row.id)}>
                      <img src={uploadIcon} alt="icon" />
                      <span> Download </span>
                    </Box>
                  </Grid>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableCell colSpan={7}>
              <div className={classes.notFound}>No data available</div>
            </TableCell>
          )
        ) : (
          <TableRow>
            <TableCell colSpan={7}>
              <div className={classes.loader}>
                <CircularProgress size={30} sx={{ color: '#DEC330' }} />
              </div>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
      <Backdrop open={openPreview} onClick={handleClose}>
        <Dialog
          open={openPreview}
          fullWidth
          onClose={handleClose}
          maxWidth={
            AllowedPdfFileTypes.includes(fileType)
              ? 'md'
              : AllowedImageFileTypes.includes(fileType)
              ? 'lg'
              : 'sm'
          }>
          <DialogTitle
            style={{
              color: '#241A2E',
              fontFamily: 'FiraSans-Regular',
              fontSize: '1.375rem',
              letterSpacing: 0,
            }}>
            <div
              style={{
                alignItems: 'flex-start',
                display: 'flex',
                justifyContent: 'space-between',
              }}>
              <span>{previewFileName}</span>
              <ClearIcon
                style={{
                  color: '#979797',
                  cursor: 'pointer',
                  fontSize: '2rem',
                }}
                color="inherit"
                component="svg"
                onClick={() => {
                  handleClose();
                }}
              />
            </div>
          </DialogTitle>
          <DialogContent>
            {url && fileType === 'pdf' ? (
              <div
                style={{
                  alignItems: 'center',
                  backgroundColor: 'transparent',
                  // border: '1px solid rgba(0, 0, 0, 0.3)',
                  display: 'flex',
                  justifyContent: 'center',
                  maxWidth: '100%',
                }}>
                <Viewer
                  fileUrl={url}
                  renderLoader={(percentages: number) => (
                    <div style={{ width: '600px' }}>
                      <ProgressBar progress={Math.round(percentages)} />
                    </div>
                  )}
                  renderPage={(props: RenderPageProps) => {
                    return (
                      <>
                        {props.canvasLayer.children}
                        <div style={{ display: 'none' }}>
                          {props.textLayer.children}
                        </div>
                        {props.annotationLayer.children}
                      </>
                    );
                  }}
                />
              </div>
            ) : url && AllowedImageFileTypes.includes(fileType) ? (
              <div
                style={{
                  alignItems: 'center',
                  backgroundColor: 'transparent',
                  border: '1px solid rgba(0, 0, 0, 0.3)',
                  display: 'flex',
                  justifyContent: 'center',
                  maxWidth: '100%',
                }}>
                <img
                  src={url}
                  alt=""
                  style={{
                    height: '100%',
                    objectFit: 'contain',
                    width: '100%',
                  }}
                />
              </div>
            ) : (
              <div
                style={{
                  alignItems: 'center',
                  border: '1px solid rgba(0, 0, 0, 0.3)',
                  color: 'GrayText',
                  display: 'flex',
                  fontSize: '1.3rem',
                  height: '100px',
                  justifyContent: 'center',
                }}>
                Please
                <div
                  style={{
                    color: '#0061FF',
                    cursor: 'pointer',
                    display: 'inline-block',
                    fontFamily: 'FiraSans-SemiBold',
                    fontSize: '1.3rem',
                    padding: '0 5px',
                    textDecoration: 'underline',
                  }}
                  onClick={() => handleDownload(fileId)}>
                  Download
                </div>
                to View this Document Type
              </div>
            )}
          </DialogContent>
        </Dialog>
      </Backdrop>
    </>
  );
};
export default observer(TableBodyContent);
